import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import shot383 from './slideshow/homeallinone.png';
import shot773 from './slideshow/773shots_so.png';
import shot511 from './slideshow/511shots_so.png';
import shot432 from './slideshow/432shots_so.png';
import shot50 from './slideshow/50shots_so.png';
import shot760 from './slideshow/newclient.png';
import notes from './slideshow/259shots_so.png';
import addclient from './slideshow/44shots_so.png';
import startbooking from './slideshow/215shots_so.png';
import location from './slideshow/720shots_so.png';
import paymentmethodsdemo from './slideshow/paymentmethodsdemo.png';
import record from './slideshow/599shots_so.png'
import analytics from './slideshow/81shots_so.png'
import createinvoice from './slideshow/createinvoice.png'
import clientreceiptinvoice from './slideshow/clientreceiptinvoice.png'


// Screens pulled from the standalone Demo page so the homepage can show the
// product itself rather than describing it. Same source images, same copy.
//
// Slides can optionally override how their device is sized:
//   deviceWidth — fraction of the stage width the phone should occupy,
//                 overriding DEVICE_WIDTH below.
//   scale       — multiplier on top of that, for small nudges (1.05, 0.95…).
// Neither is needed normally: the device is measured out of the image itself.
const showcaseSlides = [
  {
    title: 'All-in-One Solution',
    caption: 'Run your packages, bookings, and sessions from one app.',
    image: shot383,
  },
  {
    title: 'Payments Simplified',
    caption: 'Clients pay from one link, using your preferred payment methods.',
    image: clientreceiptinvoice,
  },
  {
    title: 'Automated Invoicing',
    caption: 'Send one-time invoices or set up automatic weekly and monthly billing.',
    image: createinvoice,
  },
  {
    title: 'Apple & Google Calendar Sync',
    caption: 'Your bookings automatically sync across calendars.',
    image: shot511,
  },
  {
    title: 'Easy Client Checkout',
    caption: 'Offer services through a beautifully simple mobile experience.',
    image: shot773,
  },
  {
    title: 'Flexible Location',
    caption: 'Set a location or let clients choose where the service takes place.',
    image: location,
  },
  {
    title: 'Overnight Services',
    caption: 'Offer overnight services and let clients book extended stays with ease.',
    image: shot50,
  },
  {
    title: 'Payment Methods',
    caption: 'Integrate the payment methods you already use within your dashboard.',
    image: paymentmethodsdemo,
  },
  {
    title: 'Record Payments',
    caption: 'Record payments from clients and keep your balances up to date.',
    image: record,
  },
  {
    title: 'Analytics Dashboard',
    caption: 'Track sales, revenue, and business activity all in one place.',
    image: analytics,
  },
  {
    title: 'Client Notes',
    caption: 'Track client progress, save session notes, and notify clients with text updates.',
    image: notes,
  },
  {
    title: 'Text Announcements',
    caption: 'Keep clients updated on ongoing events with text announcements.',
    image: shot432,
  },
  {
    title: 'Add Clients',
    caption: 'Add your existing clients and manage them all from one dashboard.',
    image: addclient,
  },
  {
    title: 'Start Booking Immediately',
    caption: 'Track client sessions from your dashboard and start booking right away.',
    image: startbooking,
  },
  {
    title: 'Client Portal',
    caption: 'Clients can log in to track bookings or purchase more services with you.',
    image: shot760,
  }
];

// ---------------------------------------------------------------------------
// Sizing — the Demo page's device measurement, inside a fixed-height stage.
//
// Each mockup is exported with a different amount of empty space around the
// phone, so fitting the whole canvas into the stage makes the device tiny:
// most of what's being fitted is transparent margin. Instead every image is
// measured once on load — we find the bounding box of its non-transparent
// pixels, the device — and lay out from that. Every slide then renders its
// phone at the same width, dead centre, with nothing clipped.
//
// Unlike Demo.js, the stage does NOT size itself to the devices: its height
// comes from the CSS below (780 / 660 / 540 / 470 by breakpoint), and the
// devices are sized to fit inside it. Two dials control how big they are:
// DEVICE_WIDTH sets the width, DEVICE_HEIGHT_LIMIT caps the height.
// ---------------------------------------------------------------------------

// How wide the phone itself should be, as a fraction of the stage width. This
// is the main dial: lower it to make every device smaller across the board.
const DEVICE_WIDTH = 0.56;

// The tallest a device may get, as a fraction of the stage height. If a mockup
// would exceed it, every slide scales down by the same factor so they keep
// their relative sizes instead of one getting cropped.
const DEVICE_HEIGHT_LIMIT = 0.9;

// Alpha below this counts as empty space. Bump it up if a mockup has a soft
// drop shadow that's being counted as part of the device.
const ALPHA_THRESHOLD = 24;

// Logs each mockup's dimensions and detected device box to the console.
const DEBUG_SIZING = false;

const FULL_BOX = { x: 0, y: 0, w: 1, h: 1 };

// Scans a decoded image for non-transparent pixels and returns the device box
// as fractions of the image's own width and height. Returns null if the pixels
// can't be read, or if there's no transparent margin to isolate a device from.
const scanImage = (probe) => {
  try {
    // Scan a downscaled copy — the box only needs to be accurate to within a
    // pixel or two of the final rendered size.
    const SAMPLE = 320;
    const ratio = Math.min(1, SAMPLE / Math.max(probe.naturalWidth, probe.naturalHeight));
    const w = Math.max(1, Math.round(probe.naturalWidth * ratio));
    const h = Math.max(1, Math.round(probe.naturalHeight * ratio));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(probe, 0, 0, w, h);

    const { data } = ctx.getImageData(0, 0, w, h);
    let minX = w;
    let minY = h;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (data[(y * w + x) * 4 + 3] < ALPHA_THRESHOLD) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }

    if (maxX < 0) return null;

    const box = {
      x: minX / w,
      y: minY / h,
      w: (maxX - minX + 1) / w,
      h: (maxY - minY + 1) / h,
    };

    // An edge-to-edge box means the image has no transparent margin at all, so
    // there's no device to isolate — treat it as unmeasurable and fit it whole.
    const measured = box.w < 0.98 || box.h < 0.98;
    return { box, measured };
  } catch {
    // Tainted canvas — the image loaded but can't be read.
    return null;
  }
};

// The scan runs on a separate probe image, never on the one being displayed,
// so a failure here can't affect the visible slide. It tries with crossOrigin
// first and falls back to a plain load, which is what same-origin bundled
// assets need when the dev server doesn't send CORS headers.
const measureImage = (src) =>
  new Promise((resolve) => {
    const attempt = (withCors) => {
      const probe = new Image();
      if (withCors) probe.crossOrigin = 'anonymous';
      probe.onerror = () => (withCors ? attempt(false) : resolve(null));
      probe.onload = () => {
        const result = scanImage(probe);
        if (result || !withCors) resolve(result);
        else attempt(false);
      };
      probe.src = src;
    };

    attempt(true);
  });

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  const [meta, setMeta] = useState({});
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const stageRef = useRef(null);
  const imgRefs = useRef([]);

  useEffect(() => {
    // Smooth scrolling for navigation links
    const handleAnchorClick = (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (link) {
        e.preventDefault();
        const targetId = link.getAttribute('href');
        const targetElement = document.querySelector(targetId);

        if (targetElement) {
          const navbar = document.getElementById('navbar');
          const navbarHeight = navbar ? navbar.offsetHeight : 0;
          const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - navbarHeight - 20;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });

          // Close mobile menu if open
          setMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);

    // Intersection Observer for scroll-in animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    // Nav condenses once the page is scrolled past the fold's first slice
    let scrollTimer;
    const handleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const nav = document.getElementById('navbar');
        if (!nav) return;
        if (window.pageYOffset > 50) {
          nav.classList.add('scrolled');
        } else {
          nav.classList.remove('scrolled');
        }
      }, 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('click', handleAnchorClick);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
      observer.disconnect();
    };
  }, []);

  // The stage's own box, straight off the element — so the breakpoint heights
  // in the CSS stay the source of truth for how big the container is.
  const measureStage = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    setStageSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
  }, []);

  useEffect(() => {
    measureStage();
    window.addEventListener('resize', measureStage);
    window.addEventListener('orientationchange', measureStage);
    return () => {
      window.removeEventListener('resize', measureStage);
      window.removeEventListener('orientationchange', measureStage);
    };
  }, [measureStage]);

  // Records the image's aspect ratio as soon as it decodes, so a slide can be
  // placed before the pixel scan finishes. Ratio is written LAST so a scan
  // result that landed first can't blank it out again.
  const captureRatio = useCallback((el, i) => {
    const { naturalWidth, naturalHeight } = el;
    if (!naturalWidth) return;
    const ratio = naturalHeight / naturalWidth;
    setMeta((prev) => {
      if (prev[i]?.ratio === ratio) return prev;
      return {
        ...prev,
        [i]: { box: FULL_BOX, measured: false, ...prev[i], ratio },
      };
    });
  }, []);

  // Cached images can finish decoding before React attaches onLoad, so the ref
  // callback picks up anything that's already complete.
  const registerImg = useCallback(
    (el, i) => {
      imgRefs.current[i] = el;
      if (el && el.complete) captureRatio(el, i);
    },
    [captureRatio]
  );

  // Scan every mockup once, then upgrade its entry with the real device box.
  useEffect(() => {
    let cancelled = false;

    showcaseSlides.forEach((s, i) => {
      measureImage(s.image).then((result) => {
        if (cancelled || !result) return;
        if (DEBUG_SIZING) {
          const { box } = result;
          console.log(
            `[slide ${i}] ${s.title} — device box ` +
              `x:${box.x.toFixed(3)} y:${box.y.toFixed(3)} ` +
              `w:${box.w.toFixed(3)} h:${box.h.toFixed(3)} ` +
              `measured:${result.measured}`
          );
        }
        // Merge, never replace: whatever ratio onLoad already recorded stays.
        setMeta((prev) => ({ ...prev, [i]: { ...prev[i], ...result } }));
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Turns the measured boxes into an absolute position for every image, so
  // each device lands at the same width in the same place inside the stage.
  const layout = useMemo(() => {
    const { width: stageWidth, height: stageHeight } = stageSize;
    if (!stageWidth || !stageHeight) return null;

    // Natural (unshrunk) geometry per slide, from that slide's OWN metrics.
    // A slide with no metrics yet stays null and simply isn't drawn — it never
    // borrows another slide's aspect ratio, which is what crops mockups.
    const natural = showcaseSlides.map((s, i) => {
      const m = meta[i];
      if (!m?.ratio) return null;

      // Unmeasurable mockups treat the whole canvas as the device, so they're
      // fitted whole rather than blindly trimmed.
      const box = m.measured ? m.box : FULL_BOX;
      const target = (s.deviceWidth ?? DEVICE_WIDTH) * (s.scale ?? 1);
      const imgWidth = (stageWidth * target) / box.w;
      const imgHeight = imgWidth * m.ratio;

      return { imgWidth, imgHeight, box, deviceHeight: imgHeight * box.h };
    });

    const known = natural.filter(Boolean);
    if (!known.length) return null;

    // If the tallest device won't fit the container, shrink every slide by the
    // same factor rather than letting one of them spill or get cropped.
    const room = stageHeight * DEVICE_HEIGHT_LIMIT;
    const tallest = Math.max(...known.map((g) => g.deviceHeight));
    const shrink = Math.min(1, room / tallest);

    const images = natural.map((g) => {
      if (!g) return null;

      // Guard for the window before every slide has reported in: if a late
      // arrival is taller than the others, fit it rather than let it spill.
      const fit = Math.min(1, room / (g.deviceHeight * shrink));
      const sc = shrink * fit;
      const width = g.imgWidth * sc;
      const height = g.imgHeight * sc;

      return {
        width,
        // Centre the device box — not the canvas — in the stage.
        left: stageWidth / 2 - width * (g.box.x + g.box.w / 2),
        top: stageHeight / 2 - height * (g.box.y + g.box.h / 2),
      };
    });

    return { images };
  }, [meta, stageSize]);

  // Advance the showcase on a timer. Keyed on `slide`, so any manual
  // interaction restarts the countdown instead of yanking the user forward.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSlide((i) => (i + 1) % showcaseSlides.length);
    }, 5000);
    return () => clearTimeout(timer);
  }, [slide]);

  const goToSlide = (i) => {
    const count = showcaseSlides.length;
    setSlide(((i % count) + count) % count);
  };

  const nextSlide = () => goToSlide(slide + 1);
  const prevSlide = () => goToSlide(slide - 1);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const handleTouchEnd = () => {
    const threshold = 50;
    if (touchDeltaX.current > threshold) prevSlide();
    else if (touchDeltaX.current < -threshold) nextSlide();
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  return (
    <div className="hp-root">
      {/* Navigation */}
      <nav id="navbar">
        <div className="nav-container">
          <div className="logo">
            <span className="logo-text">Verca</span>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="#features" className="nav-link">Features</a>
            <a href="#how-it-works" className="nav-link">How it works</a>
            <a href="mailto:support@appverca.com" className="nav-link nav-cta">Contact</a>
          </div>
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`fas fa-${mobileMenuOpen ? 'times' : 'bars'}`}></i>
          </button>
        </div>
      </nav>

      {/* Showcase — tagline, carousel, and downloads all inside one panel */}
      <section className="showcase" id="showcase">
        <div className="showcase-panel">
          <div className="showcase-main">
            <div
              className="showcase-stage"
              ref={stageRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="showcase-track"
                style={{ transform: `translateX(-${slide * 100}%)` }}
              >
                {showcaseSlides.map((s, i) => {
                  const placement = layout?.images[i];
                  return (
                    <div className="showcase-slide" key={i}>
                      <img
                        className="showcase-img"
                        ref={(el) => registerImg(el, i)}
                        src={s.image}
                        alt={s.title}
                        draggable="false"
                        decoding="async"
                        style={
                          placement
                            ? {
                                width: `${placement.width}px`,
                                left: `${placement.left}px`,
                                top: `${placement.top}px`,
                              }
                            : { visibility: 'hidden' }
                        }
                        onLoad={(e) => captureRatio(e.target, i)}
                      />
                    </div>
                  );
                })}
              </div>

              <button className="edge edge-left" onClick={prevSlide} aria-label="Previous screen">
                <i className="fas fa-chevron-left" />
              </button>
              <button className="edge edge-right" onClick={nextSlide} aria-label="Next screen">
                <i className="fas fa-chevron-right" />
              </button>
            </div>

            <div className="showcase-copy">
              <h2 className="showcase-title" key={`t-${slide}`}>{showcaseSlides[slide].title}</h2>
              <p className="showcase-caption" key={`c-${slide}`}>{showcaseSlides[slide].caption}</p>

              <div className="dots">
                {showcaseSlides.map((_, i) => (
                  <button
                    key={i}
                    className={`dot ${i === slide ? 'active' : ''}`}
                    onClick={() => goToSlide(i)}
                    aria-label={`Go to screen ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download CTA — fade-in line beside the store links */}
      <section className="download-cta">
        <div className="download-inner">
          <h1 className="download-text animate-on-scroll">
            Offer services through a beautifully simple mobile experience
          </h1>

          <div className="download-buttons">
            <a
              href="https://apps.apple.com/us/app/verca/id6751428364"
              className="store-btn animate-on-scroll grow-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-apple"></i>
              <div className="store-btn-text">
                <span className="store-btn-small">Download on the</span>
                <span className="store-btn-large">App Store</span>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.arthurhakobyandev.StreamChatDev"
              className="store-btn animate-on-scroll grow-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-google-play"></i>
              <div className="store-btn-text">
                <span className="store-btn-small">Get it on</span>
                <span className="store-btn-large">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="trust-strip">
        <div className="trust-indicators animate-on-scroll">
          <div className="trust-item">
            <i className="fas fa-shield-alt"></i>
            <span>Powered by Stripe</span>
          </div>
          <div className="trust-item">
            <i className="fas fa-bolt"></i>
            <span>Instant setup</span>
          </div>
          <div className="trust-item">
            <i className="fas fa-headset"></i>
            <span>Active support</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="section-header">
          <div className="section-badge animate-on-scroll">Features</div>
          <h2 className="section-title animate-on-scroll">
            Everything you need to scale your business
          </h2>
          <p className="section-subtitle animate-on-scroll">
            Built for trainers, consultants, coaches, and anyone who provides a service to repeat clientele
          </p>
        </div>

        <div className="feature-grid">
          <div className="feature-card animate-on-scroll">
            <div className="feature-icon">
              <i className="fas fa-box"></i>
            </div>
            <h3 className="feature-title">Package management</h3>
            <p className="feature-description">Create and customize service packages. Set the number of sessions, session duration, and pricing for each package.</p>
          </div>

          <div className="feature-card animate-on-scroll">
            <div className="feature-icon">
              <i className="fas fa-calendar-check"></i>
            </div>
            <h3 className="feature-title">Client scheduling</h3>
            <p className="feature-description">Let clients book sessions based on your availability. Easily manage, reschedule, or book sessions on your clients' behalf.</p>
          </div>

          <div className="feature-card animate-on-scroll">
            <div className="feature-icon">
              <i className="fas fa-clock"></i>
            </div>
            <h3 className="feature-title">Set your availability</h3>
            <p className="feature-description">Define your working hours and available time slots so clients only book when you're free.</p>
          </div>

          <div className="feature-card animate-on-scroll">
            <div className="feature-icon">
              <i className="fas fa-credit-card"></i>
            </div>
            <h3 className="feature-title">Link payments</h3>
            <p className="feature-description">Integrate the payment methods you already use with your clients — like Venmo or Zelle. Verca handles the admin work.</p>
          </div>

          <div className="feature-card animate-on-scroll">
            <div className="feature-icon">
              <i className="fas fa-user-circle"></i>
            </div>
            <h3 className="feature-title">Client portal</h3>
            <p className="feature-description">Give clients a personal portal where they can manage sessions, track their history, and book additional services.</p>
          </div>

          <div className="feature-card animate-on-scroll">
            <div className="feature-icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <h3 className="feature-title">Business insights</h3>
            <p className="feature-description">Clear insights into your business with payment analytics, client behavior trends, and your most popular packages.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-header">
          <div className="section-badge animate-on-scroll">How it works</div>
          <h2 className="section-title animate-on-scroll">
            Get started in 3 simple steps
          </h2>
          <p className="section-subtitle animate-on-scroll">
            Launch your professional service platform in minutes, not months
          </p>
        </div>

        <div className="steps-container">
          <div className="step-card animate-on-scroll">
            <div className="step-number">
              <span className="step-label">01</span>
            </div>
            <h3 className="step-title">Set up your shop</h3>
            <p className="step-description">List your services, set your prices, and customize your business profile in minutes.</p>
          </div>

          <div className="step-card animate-on-scroll">
            <div className="step-number">
              <span className="step-label">02</span>
            </div>
            <h3 className="step-title">Share your link</h3>
            <p className="step-description">Generate a link for clients to purchase your services. Your clients will receive email updates on any new services or discounts.</p>
          </div>

          <div className="step-card animate-on-scroll">
            <div className="step-number">
              <span className="step-label">03</span>
            </div>
            <h3 className="step-title">Watch your business grow</h3>
            <p className="step-description">Grow your community while saving time on tracking sessions, driving more revenue, and keeping your clients engaged.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-content">
          <div className="footer-column">
            <div className="footer-logo">
              <span className="logo-text">Verca</span>
            </div>
            <p className="footer-tagline">Powering business and clients</p>
          </div>

          <div className="footer-column">
            <h4>Legal</h4>
            <a href="privacy.html" className="footer-link">Privacy Policy</a>
            <a href="terms.html" className="footer-link">Terms of Service</a>
          </div>

          <div className="footer-column">
            <h4>Support</h4>
            <a href="mailto:support@appverca.com" className="footer-link">Contact us</a>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2025 Verca. All rights reserved.</p>
        </div>
      </footer>

      <style jsx>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-tap-highlight-color: transparent;
        }

        html {
            scroll-behavior: smooth;
        }

        :root {
            /* Near-monochrome base, one brand accent */
            --bg-primary: #FFFFFF;
            --bg-secondary: #FAFAFA;
            --bg-tertiary: #F6F6F6;

            --ink: #0F0E17;
            --text-primary: #000000;
            --text-secondary: #444444;
            --text-tertiary: #767676;

            --hairline: #E6E6E6;
            --hairline-soft: #EDEDED;

            /* Brand mint, carried over from the app + demo */
            --accent: #6BE891;
            --accent-soft: #E6FBEE;
            --accent-deep: #0B7A4B;
        }

        body {
            font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background: var(--bg-primary);
            color: var(--text-primary);
            overflow-x: hidden;
            line-height: 1.6;
            -webkit-font-smoothing: antialiased;
        }

        .hp-root {
            background: #FFFFFF;
            min-height: 100vh;
        }

        /* ── Navigation ──
           The nav's gutters match the showcase's gutters at every breakpoint,
           and .nav-container's max-width is the showcase's inner width. Those
           two things together are what keep the logo and the Contact button
           flush with the left and right edges of the dark panel below. */
        nav {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            padding: 14px 24px;
            background: #FFFFFF;
            border-bottom: 1px solid var(--hairline-soft);
            z-index: 1000;
            transition: padding 0.3s cubic-bezier(0.32, 0, 0.24, 1),
                        border-color 0.3s ease;
        }

        nav.scrolled {
            padding: 10px 24px;
            border-bottom-color: var(--hairline);
        }

        /* 1280px showcase max-width minus its 2 × 24px gutters = 1232px.
           If either of those numbers changes, this one has to change with it. */
        .nav-container {
            max-width: 1232px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .logo {
            display: flex;
            align-items: center;
            font-size: 22px;
            font-weight: 800;
            letter-spacing: -0.9px;
            cursor: pointer;
            transition: opacity 0.2s ease;
        }

        .logo:hover {
            opacity: 0.65;
        }

        .logo-text {
            color: #000000;
        }

        .nav-links {
            display: flex;
            gap: 4px;
            align-items: center;
        }

        .nav-link {
            color: var(--text-tertiary);
            text-decoration: none;
            transition: color 0.2s ease, background 0.2s ease;
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 0.2px;
            padding: 8px 12px;
            border-radius: 10px;
        }

        .nav-link:hover {
            color: #000000;
            background: #F6F6F6;
        }

        .nav-cta {
            background: #000000;
            color: #FFFFFF;
            padding: 8px 16px;
            border-radius: 10px;
            font-weight: 600;
            margin-left: 8px;
        }

        .nav-cta:hover {
            background: #1F1F1F;
            color: #FFFFFF;
        }

        /* ── Showcase (leads the page, directly under the nav) ──
           Top padding clears the fixed nav (~66px) plus a small gap. If the
           logo size or nav padding changes, this number needs a nudge too. */
        .showcase {
            padding: 78px 24px 0;
            max-width: 1280px;
            margin: 0 auto;
        }

        /* Dark panel: the mockups ship with their own dark canvas, so the
           panel matches it and the screens sit flush inside. Carousel on top,
           download links at the bottom. */
        .showcase-panel {
            background: var(--ink);
            border-radius: 28px;
            padding: 0 18px;
            display: flex;
            flex-direction: column;
            gap: 0;
            overflow: hidden;
        }

        .showcase-main {
            display: grid;
            grid-template-columns: 1.3fr 1fr;
            gap: 48px;
            align-items: center;
        }

        /* Capped and centred so the arrows hug the mockup rather than sitting
           at the far edges of the column. These heights are the container:
           the devices are sized to fit inside them, never the other way round. */
        .showcase-stage {
            position: relative;
            width: 85%;
            max-width: 500px;
            margin: 0 auto;
            height: 672px;
            overflow: hidden;
            border-radius: 20px;
            touch-action: pan-y;
        }

        .showcase-track {
            display: flex;
            height: 100%;
            transition: transform 0.5s cubic-bezier(0.32, 0, 0.24, 1);
        }

        .showcase-slide {
            position: relative;
            min-width: 100%;
            height: 100%;
            overflow: hidden;
        }

        /* Positioned from the measured device box rather than the canvas, so
           the empty margin falls outside the stage and gets clipped while the
           device stays whole and centred. Aspect ratio is never touched: the
           width is set and the height follows. */
        .showcase-img {
            position: absolute;
            display: block;
            height: auto;
            max-width: none;
            user-select: none;
            transition: width 0.5s cubic-bezier(0.32, 0, 0.24, 1),
                        left 0.5s cubic-bezier(0.32, 0, 0.24, 1),
                        top 0.5s cubic-bezier(0.32, 0, 0.24, 1);
        }

        .edge {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            width: 44px;
            height: 44px;
            border-radius: 50%;
            border: 1px solid rgba(255, 255, 255, 0.22);
            background: rgba(255, 255, 255, 0.06);
            color: #FFFFFF;
            font-size: 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease;
            z-index: 2;
        }

        .edge-left { left: 0; }
        .edge-right { right: 0; }

        .edge:hover {
            background: var(--accent);
            border-color: var(--accent);
            color: #0A0A0F;
        }

        .edge:focus-visible {
            outline: 2px solid var(--accent);
            outline-offset: 2px;
        }

        .showcase-copy {
            color: #FFFFFF;
        }

        .showcase-title {
            font-size: clamp(34px, 3.6vw, 50px);
            font-weight: 800;
            letter-spacing: -1.5px;
            line-height: 1.08;
            margin-bottom: 18px;
            color: #FFFFFF;
            animation: rise 0.45s cubic-bezier(0.32, 0, 0.24, 1) both;
        }

        .showcase-caption {
            font-size: clamp(21px, 1.8vw, 26px);
            line-height: 1.5;
            color: rgba(255, 255, 255, 0.7);
            margin-bottom: 32px;
            min-height: 78px;
            animation: rise 0.45s cubic-bezier(0.32, 0, 0.24, 1) both;
        }

        .dots {
            display: flex;
            gap: 8px;
            margin-bottom: 0;
            flex-wrap: wrap;
        }

        .dot {
            width: 9px;
            height: 9px;
            border-radius: 50%;
            border: none;
            padding: 0;
            background: rgba(255, 255, 255, 0.24);
            cursor: pointer;
            transition: background 0.3s ease, width 0.3s ease;
        }

        .dot:hover { background: rgba(255, 255, 255, 0.45); }

        .dot.active {
            width: 28px;
            border-radius: 5px;
            background: var(--accent);
        }

        .dot:focus-visible {
            outline: 2px solid var(--accent);
            outline-offset: 3px;
        }

        @keyframes rise {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        /* ── Download CTA (below the panel: text left, store links right) ── */
        .download-cta {
            padding: 56px 24px 4px;
            max-width: 1400px;
            margin: 0 auto;
        }

        .download-inner {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 30px;
        }

        /* Script face for the tagline. Dancing Script is a connected cursive
           that still reads cleanly at large sizes — scripts need looser
           tracking and a bit more size than the sans to stay legible. */
        .download-text {
            max-width: 900px;
            font-family: 'Dancing Script', 'Brush Script MT', cursive;
            font-size: clamp(30px, 3.6vw, 50px);
            font-weight: 400;
            line-height: 1.3;
            letter-spacing: 0.2px;
            color: #A8A8A8;
            margin: 0;
        }

        .download-buttons {
            display: flex;
            flex-direction: row;
            gap: 14px;
            justify-content: center;
            flex-wrap: wrap;
        }

        .store-btn {
            display: inline-flex;
            align-items: center;
            gap: 12px;
            padding: 14px 26px;
            background: #000000;
            border: 1px solid #000000;
            border-radius: 14px;
            color: #FFFFFF;
            text-decoration: none;
            min-width: 200px;
        }

        .store-btn:focus-visible {
            outline: 2px solid #000000;
            outline-offset: 3px;
        }

        .store-btn i {
            font-size: 28px;
            flex-shrink: 0;
            color: currentColor;
        }

        .store-btn-text {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }

        .store-btn-small {
            font-size: 11px;
            font-weight: 500;
            color: currentColor;
            opacity: 0.7;
            letter-spacing: 0.4px;
            line-height: 1.2;
            text-transform: uppercase;
        }

        .store-btn-large {
            font-size: 18px;
            font-weight: 600;
            color: currentColor;
            letter-spacing: 0.2px;
            line-height: 1.25;
        }

        /* Grow-in: the links scale up into place as the section enters view,
           then grow a little further on hover. Overrides the shared
           translateY reveal used by .animate-on-scroll elsewhere. */
        .animate-on-scroll.grow-btn {
            opacity: 0;
            transform: scale(0.8);
            transition: opacity 0.5s cubic-bezier(0.32, 0, 0.24, 1),
                        transform 0.6s cubic-bezier(0.34, 1.45, 0.64, 1),
                        background 0.25s ease, border-color 0.25s ease;
        }

        .animate-on-scroll.grow-btn.visible {
            opacity: 1;
            transform: scale(1);
        }

        .animate-on-scroll.grow-btn.visible:hover {
            transform: scale(1.06);
            background: #1F1F1F;
            border-color: #1F1F1F;
        }

        /* ── Trust strip ── */
        .trust-strip {
            padding: 44px 24px 8px;
        }

        .trust-indicators {
            display: flex;
            gap: 32px;
            justify-content: center;
            flex-wrap: wrap;
        }

        .trust-item {
            display: flex;
            align-items: center;
            gap: 10px;
            color: var(--text-tertiary);
            font-size: 15px;
            font-weight: 500;
        }

        .trust-item i {
            color: var(--accent-deep);
            font-size: 15px;
        }

        /* ── Section Header ── */
        .section-header {
            text-align: center;
            margin-bottom: 56px;
        }

        .section-badge {
            display: inline-flex;
            align-items: center;
            padding: 6px 16px;
            background: var(--accent-soft);
            color: var(--accent-deep);
            border-radius: 50px;
            font-size: 12px;
            font-weight: 700;
            margin-bottom: 24px;
            letter-spacing: 1.5px;
            text-transform: uppercase;
        }

        .section-title {
            font-size: clamp(32px, 4.5vw, 52px);
            font-weight: 800;
            margin-bottom: 20px;
            letter-spacing: -1.5px;
            line-height: 1.15;
            color: #000000;
        }

        .section-subtitle {
            font-size: 17px;
            color: var(--text-tertiary);
            max-width: 620px;
            margin: 0 auto;
            letter-spacing: 0.2px;
            line-height: 1.6;
        }

        /* ── Features ── */
        .features {
            padding: 90px 40px 100px;
            max-width: 1200px;
            margin: 0 auto;
        }

        .feature-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
            gap: 24px;
        }

        .feature-card {
            background: #FFFFFF;
            border: 1px solid var(--hairline);
            border-radius: 20px;
            padding: 40px 34px;
            transition: border-color 0.3s ease, background 0.3s ease, transform 0.3s cubic-bezier(0.32, 0, 0.24, 1);
        }

        .feature-card:hover {
            transform: translateY(-4px);
            border-color: var(--accent);
            background: #FCFFFD;
        }

        .feature-icon {
            width: 56px;
            height: 56px;
            background: var(--accent);
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 24px;
            transition: transform 0.3s ease;
        }

        .feature-card:hover .feature-icon {
            transform: scale(1.06);
        }

        .feature-icon i {
            font-size: 22px;
            color: #0A0A0F;
        }

        .feature-title {
            font-size: 21px;
            font-weight: 700;
            margin-bottom: 12px;
            letter-spacing: -0.4px;
            color: #000000;
        }

        .feature-description {
            color: var(--text-tertiary);
            line-height: 1.7;
            font-size: 16px;
            font-weight: 400;
            letter-spacing: 0.1px;
        }

        /* ── How It Works ── */
        .how-it-works {
            padding: 100px 40px;
            background: #FAFAFA;
            border-top: 1px solid var(--hairline-soft);
            border-bottom: 1px solid var(--hairline-soft);
        }

        .steps-container {
            max-width: 1080px;
            margin: 0 auto;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
        }

        .step-card {
            text-align: center;
            padding: 40px 28px;
            background: #FFFFFF;
            border: 1px solid var(--hairline);
            border-radius: 20px;
            transition: border-color 0.3s ease, transform 0.3s cubic-bezier(0.32, 0, 0.24, 1);
        }

        .step-card:hover {
            transform: translateY(-4px);
            border-color: var(--accent);
        }

        .step-number {
            width: 64px;
            height: 64px;
            margin: 0 auto 24px;
            background: var(--accent-soft);
            border: 2px solid var(--accent);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.3s ease;
        }

        .step-card:hover .step-number {
            background: var(--accent);
        }

        .step-label {
            font-size: 18px;
            font-weight: 800;
            color: var(--accent-deep);
            letter-spacing: 0.5px;
            transition: color 0.3s ease;
        }

        .step-card:hover .step-label {
            color: #0A0A0F;
        }

        .step-title {
            font-size: 20px;
            font-weight: 700;
            margin-bottom: 12px;
            letter-spacing: -0.4px;
            color: #000000;
        }

        .step-description {
            color: var(--text-tertiary);
            line-height: 1.65;
            font-size: 15px;
            letter-spacing: 0.2px;
        }

        /* ── Footer ── */
        footer {
            padding: 72px 40px 36px;
            background: #FFFFFF;
            border-top: 1px solid var(--hairline);
        }

        .footer-content {
            max-width: 1200px;
            margin: 0 auto 44px;
            display: grid;
            grid-template-columns: 2fr 1fr 1fr;
            gap: 44px;
        }

        .footer-logo {
            display: flex;
            align-items: center;
            margin-bottom: 14px;
            font-size: 24px;
            font-weight: 800;
            letter-spacing: -1px;
        }

        .footer-tagline {
            color: var(--text-tertiary);
            font-size: 15px;
        }

        .footer-column h4 {
            font-size: 13px;
            margin-bottom: 20px;
            color: var(--text-tertiary);
            text-transform: uppercase;
            letter-spacing: 1.2px;
            font-weight: 700;
        }

        .footer-link {
            display: block;
            color: var(--text-secondary);
            text-decoration: none;
            margin-bottom: 12px;
            transition: color 0.2s ease;
            font-size: 15px;
        }

        .footer-link:hover {
            color: var(--accent-deep);
        }

        .footer-bottom {
            max-width: 1200px;
            margin: 0 auto;
            padding-top: 32px;
            border-top: 1px solid var(--hairline-soft);
            text-align: center;
            color: var(--text-tertiary);
            font-size: 14px;
        }

        /* ── Mobile menu button ── */
        .mobile-menu-btn {
            display: none;
            background: #FFFFFF;
            border: 1px solid #D6D6D6;
            color: #000000;
            font-size: 17px;
            cursor: pointer;
            padding: 8px 12px;
            border-radius: 10px;
            transition: background 0.2s ease, border-color 0.2s ease;
        }

        .mobile-menu-btn:hover {
            background: #F6F6F6;
            border-color: #000000;
        }

        /* ── Scroll-in transitions ── */
        .animate-on-scroll {
            opacity: 0;
            transform: translateY(16px);
            transition: opacity 0.6s cubic-bezier(0.32, 0, 0.24, 1),
                        transform 0.6s cubic-bezier(0.32, 0, 0.24, 1);
            will-change: opacity, transform;
        }

        .animate-on-scroll.visible {
            opacity: 1;
            transform: translateY(0);
        }

        /* Stagger */
        .animate-on-scroll:nth-child(1) { transition-delay: 0s; }
        .animate-on-scroll:nth-child(2) { transition-delay: 0.06s; }
        .animate-on-scroll:nth-child(3) { transition-delay: 0.12s; }
        .animate-on-scroll:nth-child(4) { transition-delay: 0.18s; }
        .animate-on-scroll:nth-child(5) { transition-delay: 0.24s; }
        .animate-on-scroll:nth-child(6) { transition-delay: 0.3s; }

        @media (prefers-reduced-motion: reduce) {
            .animate-on-scroll {
                transition-duration: 1ms;
                transform: none;
            }
            .showcase-track,
            .showcase-img,
            .showcase-title,
            .showcase-caption {
                transition: none;
                animation: none;
            }
            .animate-on-scroll.grow-btn,
            .animate-on-scroll.grow-btn.visible,
            .animate-on-scroll.grow-btn.visible:hover {
                transform: none;
                transition-duration: 1ms;
            }
            .feature-card:hover,
            .step-card:hover {
                transform: none;
            }
            .feature-card:hover .feature-icon {
                transform: none;
            }
        }

        /* ── Responsive ──
           Nav gutters and showcase gutters move together at every breakpoint
           so the header stays flush with the panel edges as the page narrows. */
        @media (max-width: 1024px) {
            nav {
                padding: 14px 20px;
            }

            nav.scrolled {
                padding: 10px 20px;
            }

            .showcase {
                padding: 78px 20px 0;
            }

            .showcase-main {
                grid-template-columns: 1fr;
                gap: 30px;
            }

            .showcase-copy {
                text-align: center;
            }

            .dots {
                justify-content: center;
            }

            .showcase-stage {
                height: 560px;
            }

            .showcase-caption {
                min-height: 0;
            }
        }

        @media (max-width: 900px) {
            .steps-container {
                grid-template-columns: 1fr;
                max-width: 520px;
            }
        }

        @media (max-width: 768px) {
            nav {
                padding: 12px 16px;
            }

            nav.scrolled {
                padding: 10px 16px;
            }

            .mobile-menu-btn {
                display: block;
            }

            .nav-links {
                display: none;
                position: absolute;
                top: 100%;
                left: 0;
                right: 0;
                background: #FFFFFF;
                flex-direction: column;
                padding: 16px 20px 20px;
                gap: 6px;
                border-bottom: 1px solid var(--hairline);
            }

            .nav-links.mobile-open {
                display: flex;
            }

            .nav-link {
                width: 100%;
                text-align: center;
            }

            .nav-cta {
                margin-left: 0;
                margin-top: 4px;
            }

            .showcase {
                padding: 72px 16px 0;
            }

            /* Mobile browsers: the panel stacks and sits closer to the screen
               edges, so it needs vertical breathing room the desktop layout
               doesn't. */
            .showcase-panel {
                padding: 20px 10px 24px;
                border-radius: 22px;
                gap: 0;
            }

            .showcase-stage {
                height: 432px;
            }

            .download-cta {
                padding: 44px 20px 0;
            }

            .download-inner {
                gap: 24px;
            }

            .trust-strip {
                padding: 36px 20px 0;
            }

            .features {
                padding: 72px 20px;
            }

            .how-it-works {
                padding: 72px 20px;
            }

            .section-header {
                margin-bottom: 40px;
            }

            .feature-grid {
                grid-template-columns: 1fr;
            }

            .trust-indicators {
                gap: 18px;
            }

            .trust-item {
                font-size: 14px;
            }

            .footer-content {
                grid-template-columns: 1fr;
                text-align: center;
            }

            .footer-logo {
                justify-content: center;
            }
        }

        @media (max-width: 480px) {
            .showcase-stage {
                height: 376px;
            }

            .download-buttons {
                flex-direction: column;
                align-items: center;
                width: 100%;
            }

            .store-btn {
                width: 100%;
                max-width: 320px;
                justify-content: center;
            }
        }
      `}</style>

      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;0,9..40,900&family=Dancing+Script:wght@400;500;600;700&display=swap" rel="stylesheet" />
    </div>
  );
};

export default HomePage;